import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiFileText, FiTarget, FiSearch, FiList } = FiIcons;

const Deliverables = () => {
  const items = [
    { icon: FiSearch, text: "What is weakening your visibility right now" },
    { icon: FiList, text: "What trust signals are missing online" },
    { icon: FiTarget, text: "What needs to be fixed first (Priority Roadmap)" },
    { icon: FiCheck, text: "Where your best growth opportunities are hiding" },
    { icon: FiFileText, text: "How to rebuild online authority in the AI search era" }
  ];

  return (
    <section id="deliverables" className="py-24 relative overflow-hidden">
      {/* Decorative glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/5 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center relative z-10">
        
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
            Stop Guessing. Get a Clear Action Plan.
          </h2>
          <p className="text-xl text-slate-400 mb-10 leading-relaxed">
            We don't do automated generic checklists. You get a personalized, human-reviewed strategy showing exactly what's broken and what to do next.
          </p>
          
          <ul className="space-y-6 mb-10">
            {items.map((item, i) => (
              <motion.li 
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center glass-card rounded-lg p-4"
              >
                <div className="bg-teal/20 p-2 rounded mr-4">
                  <SafeIcon icon={item.icon} className="text-teal text-xl" />
                </div>
                <span className="text-white font-semibold text-lg">{item.text}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative"
        >
          {/* Audit Report Mockup */}
          <div className="glass-card rounded-2xl p-8 border-t-4 border-t-teal shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal/10 blur-3xl rounded-full"></div>
            
            <div className="flex justify-between items-center border-b border-white/10 pb-6 mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">Trust Architecture Audit</h3>
                <p className="text-teal text-sm font-semibold uppercase tracking-wider">Confidential Report</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-white">42<span className="text-xl text-slate-500">/100</span></div>
                <p className="text-xs text-slate-400 mt-1">Current Trust Score</p>
              </div>
            </div>

            <div className="space-y-5">
              <MockupBar label="Authority & Backlinks" percent="35" color="bg-rose-500" />
              <MockupBar label="E-E-A-T & Credentials" percent="60" color="bg-amber-400" />
              <MockupBar label="Entity & Schema Clarity" percent="45" color="bg-rose-500" />
              <MockupBar label="Content Freshness" percent="20" color="bg-rose-500" />
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="bg-navy-800 rounded-lg p-4 border border-white/5">
                <p className="text-sm text-slate-300"><strong className="text-white block mb-1">Top Priority Fix:</strong> Your business entity lacks clear schema definition across major service pages, causing AI tools to bypass you for local recommendations.</p>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

const MockupBar = ({ label, percent, color }) => (
  <div>
    <div className="flex justify-between text-sm mb-2 font-medium">
      <span className="text-slate-300">{label}</span>
      <span className="text-white">{percent}%</span>
    </div>
    <div className="h-2 w-full bg-navy-800 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${percent}%` }}></div>
    </div>
  </div>
);

export default Deliverables;